/* eslint-disable react-hooks/refs */
import Box, { type BoxProps } from "@mui/material/Box";
import Slide from "@mui/material/Slide";
import {
  LanguageTabs,
  type LanguageTabsProps,
} from "core/components/LanguageTabs";
import type { SelectOption } from "core/entities";
import { type ReactNode, useMemo, useRef, useState } from "react";
import {
  type ArrayPath,
  type Control,
  type FieldPath,
  type FieldValues,
  type UseFieldArrayProps,
  useController,
  useFieldArray,
} from "react-hook-form";
import { TranslationFieldErrorMessage } from "../TranslationFieldErrorMessage";

function invertDirection(input: "left" | "right"): "left" | "right" {
  return input === "left" ? "right" : "left";
}

export type ReactHookArrayTabsProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends ArrayPath<TFieldValues> = ArrayPath<TFieldValues>,
> = {
  localeOptions: SelectOption[];
  name: TName;
  currentTabValueName: FieldPath<TFieldValues>;
  translationFieldLocaleKey: keyof TFieldValues[TName][number];
  render: (
    index: number,
    control: Control<TFieldValues>,
    currentOption: SelectOption
  ) => ReactNode;
  getNewLocaleObject: (locale: string) => TFieldValues[TName][number];
  control: Control<TFieldValues>;
  boxProps?: BoxProps;
  onTabAdd?: (tabValue: string) => void;
  onTabRemove?: (removedTabValue: string, nextTabValue: string) => void;
  rules?: UseFieldArrayProps<TFieldValues, TName>["rules"];
  variant?: LanguageTabsProps["tabVariant"];
};

/**
 * ReactHookArrayTabs component provides a tabbed interface for managing an array of form fields
 * using React Hook Form's `useFieldArray` and `useController` hooks.
 *
 * @template TFieldValues - The type of the field values.
 * @template TName - The type of the array path.
 *
 * @param {Object} props - The props for the ReactHookArrayTabs component.
 * @param {string} props.currentTabValueName - The name of the current tab value field.
 * @param {Array<SelectOption>} props.localeOptions - The available locale options for the tabs.
 * @param {TName} props.name - The name of the field array.
 * @param {Control<TFieldValues>} props.control - The control object from React Hook Form.
 * @param {Function} props.getNewLocaleObject - A function to get a new locale object when adding a new tab.
 * @param {Function} props.render - A render function to render the content of each tab.
 * @param {string} props.translationFieldLocaleKey - The key to access the locale value in the field object.
 * @param {Object} [props.boxProps] - Additional props to pass to the outer Box component.
 * @param {Function} [props.onTabAdd] - A callback function to be called when a new tab is added.
 * @param {Function} [props.onTabRemove] - A callback function to be called when a tab is removed.
 *
 * @example
 * ```tsx
 * const formMethods = useForm({
 *   defaultValues:{
 *     translations:[{locale:"en", title:"Title"}]
 *   }
 * });
 * return (
 * <Box component="form" onSubmit={formMethods.handleSubmit(onSubmit)}>
 *  <ReactHookArrayTabs
 *    control={formMethods.control}
 *    currentTabValueName="locale"
 *    localeOptions={[{label:"English", value:"en"}, {label:"French", value:"fr"}]}
 *    name="translations"
 *    getNewLocaleObject={(locale) => ({locale, title:""})}
 *    translationFieldLocaleKey="locale"
 *    render={(index, control, currentOption) => (
 *      <ReactHookTextField
 *       required
 *       control={control}
 *       name={`translations.${index}.title`}
 *       label={`Title (${currentOption.label})`}
 *      />
 *    )}
 *   />
 * </Box>
 * )
 * ```
 */
export function ReactHookArrayTabs<
  TFieldValues extends FieldValues = FieldValues,
  TName extends ArrayPath<TFieldValues> = ArrayPath<TFieldValues>,
>({
  currentTabValueName,
  localeOptions,
  name,
  control,
  getNewLocaleObject,
  render,
  translationFieldLocaleKey,
  boxProps,
  onTabAdd,
  onTabRemove,
  rules,
  variant = "styled",
}: ReactHookArrayTabsProps<TFieldValues, TName>) {
  const { fields, append, remove } = useFieldArray({
    control,
    name,
    rules,
  });
  const containerRef = useRef<HTMLDivElement>(null);

  const currentTabs = fields
    .map((field) =>
      localeOptions.find(
        (option) => option.value === field[translationFieldLocaleKey]
      )
    )
    .filter((option): option is SelectOption => Boolean(option));

  const unselectedTabs = localeOptions.filter(
    (option) =>
      !fields.some((field) => field[translationFieldLocaleKey] === option.value)
  );
  const {
    field: { onChange: updateCurrentTabValue, value: currentTabValue },
  } = useController({ control, name: currentTabValueName });

  const [previousTabValue, setPreviewTabValue] = useState(currentTabValue);

  const tabMovementDirection = useMemo(() => {
    const currentIndex = currentTabs.findIndex(
      (option) => option.value === currentTabValue
    );
    const previousIndex = currentTabs.findIndex(
      (option) => option.value === previousTabValue
    );
    const direction = currentIndex > previousIndex ? "right" : "left";
    return direction;
  }, [currentTabValue, currentTabs, previousTabValue]);

  return (
    <Box {...boxProps}>
      <TranslationFieldErrorMessage
        control={control}
        name={name}
        translationFieldLocaleKey={translationFieldLocaleKey}
        sx={{ mb: 1 }}
      />
      <LanguageTabs
        tabVariant={variant}
        handleTabChange={(_, value) => {
          setPreviewTabValue(currentTabValue);
          updateCurrentTabValue(value);
        }}
        currentTabs={currentTabs}
        tabValue={currentTabValue}
        unselectedTabs={unselectedTabs}
        removeTabOnClick={(tabValue: string) => {
          const fieldsIndex = fields.findIndex(
            (field) => field[translationFieldLocaleKey] === tabValue
          );
          const currentTabsIndex = currentTabs.findIndex(
            (option) => option.value === tabValue
          );
          const nextTabValue = currentTabs.at(currentTabsIndex - 1)?.value;
          if (!nextTabValue) {
            throw new Error("No next tab value found");
          }
          updateCurrentTabValue(nextTabValue);
          remove(fieldsIndex);
          if (typeof onTabRemove === "function") {
            onTabRemove(tabValue, nextTabValue);
          }
        }}
        selectAddTabValue={(tabValue) => {
          append(getNewLocaleObject(tabValue));
          if (typeof onTabAdd === "function") {
            onTabAdd(tabValue);
          }
        }}
      />
      <Box
        ref={containerRef}
        sx={{
          display: "grid",
          gridTemplateAreas: "'stack'",
          mt: 2,
          overflowX: "clip",
        }}
      >
        {fields.map((field, index) => {
          const isCurrentTab =
            currentTabValue === field[translationFieldLocaleKey];
          const slideDirection = isCurrentTab
            ? tabMovementDirection
            : invertDirection(tabMovementDirection);

          return (
            <Slide
              key={field.id}
              appear={index !== 0}
              in={currentTabValue === field[translationFieldLocaleKey]}
              style={{ gridArea: "stack" }}
              direction={slideDirection}
              container={containerRef.current}
            >
              <Box>
                {render(
                  index,
                  control,
                  localeOptions.find(
                    (option) =>
                      option.value === field[translationFieldLocaleKey]
                  ) ?? { label: "Unknown", value: "unknown" }
                )}
              </Box>
            </Slide>
          );
        })}
      </Box>
    </Box>
  );
}
